import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Dropdown } from "react-bootstrap";
import {
    CalendarDays,
    CloudSun,
    BarChart3,
    Grid2X2,
    ChevronLeft,
    X,
} from "lucide-react";
import { CalendarData } from "./calendar";
import { WeatherInfo } from "./WeatherInfo";
import { KpiDashboard } from "./KpiDashboard";
import "./AppsLauncher.css";

type WidgetPopup = "calendar" | "weather" | "kpi" | null;

interface AppItem {
    title: string;
    description: string;
    icon: React.ReactNode;
    iconClass: string;
    onClick?: () => void;
}

interface WidgetShellProps {
    title: string;
    onBack: () => void;
    onClose: () => void;
    children: ReactNode;
}

const WidgetShell = ({ title, onBack, onClose, children }: WidgetShellProps) => (
    <div className="apps-widget-shell">
        <div className="apps-widget-header">
            <button
                type="button"
                className="apps-widget-back-button"
                aria-label="Back to widgets"
                title="Back to widgets"
                onClick={onBack}
            >
                <ChevronLeft size={18} />
                <span>Back</span>
            </button>

            <h4>{title}</h4>

            <button
                type="button"
                className="apps-close-button"
                aria-label={`Close ${title}`}
                onClick={onClose}
            >
                <X size={18} />
            </button>
        </div>
        <div className="apps-widget-body">{children}</div>
    </div>
);

const AppsLauncher = () => {
    const [appsOpen, setAppsOpen] = useState(false);
    const [activeWidget, setActiveWidget] = useState<WidgetPopup>(null);
    const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
    const launcherRef = useRef<HTMLDivElement>(null);

    const closeWidget = useCallback(() => {
        setActiveWidget(null);
        setCurrentDate(new Date());
    }, []);

    const backToWidgets = useCallback(() => {
        setActiveWidget(null);
        setCurrentDate(new Date());
        window.setTimeout(() => {
            setAppsOpen(true);
        }, 0);
    }, []);

    const openWidget = useCallback((widget: Exclude<WidgetPopup, null>) => {
        setAppsOpen(false);
        if (widget === "calendar") {
            setCurrentDate(new Date());
        }
        // Let the widgets dropdown unmount before opening the next popup
        window.setTimeout(() => {
            setActiveWidget(widget);
        }, 0);
    }, []);

    useEffect(() => {
        if (!activeWidget) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target || !launcherRef.current) {
                return;
            }

            // Keep open when interacting with Bootstrap modals (EventModal portal)
            if ((target as Element).closest?.(".modal")) {
                return;
            }

            if (!launcherRef.current.contains(target)) {
                closeWidget();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                backToWidgets();
            }
        };

        const syncWidgetPopupPosition = () => {
            if (!launcherRef.current) {
                return;
            }

            const rect = launcherRef.current.getBoundingClientRect();
            launcherRef.current.style.setProperty(
                "--apps-widget-top",
                `${Math.round(rect.bottom + 12)}px`
            );
        };

        syncWidgetPopupPosition();
        window.addEventListener("resize", syncWidgetPopupPosition);
        window.addEventListener("scroll", syncWidgetPopupPosition, true);
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("resize", syncWidgetPopupPosition);
            window.removeEventListener("scroll", syncWidgetPopupPosition, true);
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [activeWidget, closeWidget, backToWidgets]);

    const isOverlayOpen = appsOpen || Boolean(activeWidget);

    const closeOverlay = useCallback(() => {
        setAppsOpen(false);
        closeWidget();
    }, [closeWidget]);

    const apps: AppItem[] = [
        {
            title: "Calendar",
            description: "Events & important dates",
            icon: <CalendarDays size={26} />,
            iconClass: "apps-icon-calendar",
            onClick: () => openWidget("calendar"),
        },
        {
            title: "Weather",
            description: "Current weather updates",
            icon: <CloudSun size={28} />,
            iconClass: "apps-icon-weather",
            onClick: () => openWidget("weather"),
        },
        {
            title: "KPI Dashboard",
            description: "Key performance indicators",
            icon: <BarChart3 size={27} />,
            iconClass: "apps-icon-kpi",
            onClick: () => openWidget("kpi"),
        },
    ];

    return (
        <>
            {isOverlayOpen && (
                <div
                    className="apps-launcher-backdrop"
                    aria-hidden="true"
                    onClick={closeOverlay}
                />
            )}

            <div
                className={`apps-launcher${isOverlayOpen ? " apps-launcher-open" : ""}`}
                ref={launcherRef}
            >
                <Dropdown
                    show={appsOpen}
                    onToggle={(isOpen) => {
                        if (activeWidget) {
                            closeWidget();
                            return;
                        }
                        setAppsOpen(isOpen);
                    }}
                    align="end"
                    autoClose="outside"
                >
                    <Dropdown.Toggle
                        as="button"
                        className={`apps-launcher-button ${
                            appsOpen || activeWidget
                                ? "apps-launcher-button-active"
                                : ""
                        }`}
                    >
                        <Grid2X2 size={22} strokeWidth={2} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="apps-dropdown-menu">
                        <div className="apps-dropdown-content">
                            <div className="apps-dropdown-header">
                                <div>
                                    <h4>Widgets</h4>
                                </div>

                                <button
                                    type="button"
                                    className="apps-close-button"
                                    aria-label="Close widgets menu"
                                    onClick={() => setAppsOpen(false)}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="apps-grid">
                                {apps.map((app) => (
                                    <button
                                        type="button"
                                        key={app.title}
                                        className="app-item"
                                        onClick={app.onClick}
                                    >
                                        <div className={`app-icon ${app.iconClass}`}>
                                            {app.icon}
                                        </div>
                                        <div className="app-title">{app.title}</div>
                                        <div className="app-description">
                                            {app.description}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>

                {activeWidget === "calendar" && (
                    <div
                        className="apps-widget-popup"
                        role="dialog"
                        aria-label="Calendar"
                    >
                        <WidgetShell
                            title="Calendar"
                            onBack={backToWidgets}
                            onClose={closeOverlay}
                        >
                            <CalendarData
                                embedded
                                onClose={closeWidget}
                                currentDate={currentDate}
                                setCurrentDate={setCurrentDate}
                            />
                        </WidgetShell>
                    </div>
                )}

                {activeWidget === "weather" && (
                    <div
                        className="apps-widget-popup"
                        role="dialog"
                        aria-label="Weather"
                    >
                        <WidgetShell
                            title="Weather"
                            onBack={backToWidgets}
                            onClose={closeOverlay}
                        >
                            <WeatherInfo />
                        </WidgetShell>
                    </div>
                )}

                {activeWidget === "kpi" && (
                    <div
                        className="apps-widget-popup"
                        role="dialog"
                        aria-label="KPI Dashboard"
                    >
                        <WidgetShell
                            title="KPI Dashboard"
                            onBack={backToWidgets}
                            onClose={closeOverlay}
                        >
                            <KpiDashboard />
                        </WidgetShell>
                    </div>
                )}
            </div>
        </>
    );
};

export default AppsLauncher;
